import { AppPhase, DealingSubPhase } from '../../models/communication/app-enums.model';
import { GameChangeEvent } from '../../models/communication/app-state.model';
import { GameBase } from '../game-base';
import { GameFlowBase } from '../game-flow-base';
import { CardState, SideState } from '../models/game-view.model';

export const openGameStateNames = ['START', 'FACE-DOWN-CARD', 'SIDE-SELECTION-ANIMATION', 'REVEAL-CARDS', 'OUTCOME'] as const;
export type OpenGameStateName = (typeof openGameStateNames)[number];

export class OpenGameFlow extends GameFlowBase<GameBase, GameChangeEvent, OpenGameStateName> {
    private static readonly sideSelectAnimationIntervalMs: number = 330;

    protected override stateNames = openGameStateNames;

    private animationInterval!: ReturnType<typeof setInterval>;

    onChange({ action, data }: GameChangeEvent): void {
        switch (this.getState()) {
            case 'START': {
                if (action === 'appPhaseChange' && data === AppPhase.NewRoundPreparation) {
                    this.game.showFaceDownMessage(this.game.getFirstCardPosition());
                    this.nextState();
                }
                break;
            }

            case 'FACE-DOWN-CARD': {
                if (data === DealingSubPhase.SideSelectionAnimation) {
                    this.game.updateVisibleCards(CardState.Back);
                    this.startSideSelectAnimation();
                    this.nextState();
                    return;
                }

                if (action === 'tableChange') {
                    this.game.updateCard(data.cardPosition, CardState.Checked);

                    if (data.nextCardPosition === undefined) {
                        this.game.showAllCardsDrawn();
                    } else {
                        this.game.showFaceDownMessage(data.nextCardPosition);
                    }
                }

                break;
            }

            case 'SIDE-SELECTION-ANIMATION': {
                if (data === DealingSubPhase.LeftSide || data === DealingSubPhase.RightSide) {
                    this.stopSideSelectAnimation();
                    this.nextState();
                }
                break;
            }

            case 'REVEAL-CARDS': {
                if (data === AppPhase.Decision) {
                    this.game.showPlayerDecisionTime();
                    return;
                }

                if (action === 'tableChange') {
                    this.game.revealCard(data.cardPosition, data.card);
                    this.game.showHandValue(data);

                    if (data.nextCardPosition !== undefined) {
                        this.game.showFaceUpMessage(data.nextCardPosition);
                    }

                    if (data.outcome !== undefined) {
                        this.nextState();
                    }
                }

                break;
            }

            case 'OUTCOME': {
                if (action === 'tableChange') {
                    this.game.handleOutcome(data);
                }
            }
        }
    }

    startSideSelectAnimation(): void {
        this.game.updateTitle($localize`Side selection time`, { isSpecial: true });
        this.animationInterval = this.setSideSelectInterval();
    }

    stopSideSelectAnimation(): void {
        clearInterval(this.animationInterval);
    }

    setSideSelectInterval(): ReturnType<typeof setInterval> {
        let sideIndex = 0;
        const { length } = this.game.view.sides;

        const animate = (): void => {
            this.game.view.sides[sideIndex].state = SideState.Current;
            sideIndex = (sideIndex + 1) % length;
            this.game.view.sides[sideIndex].state = SideState.Muted;
        };

        animate();
        return setInterval(animate, OpenGameFlow.sideSelectAnimationIntervalMs);
    }
}
